import React, { useState, useMemo } from 'react';
import { useTable, useSortBy } from 'react-table';
import { useNavigate, useParams } from 'react-router-dom';
import Global from "../../utils/Global";
import { ChevronUp, ChevronDown, Calendar, Clock } from 'react-feather';

const DropdownTable = ({ index, title, data, report = null }) => {
  const [open, setOpen] = useState(index === 0);
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null); // State for tracking hovered row index
  const navigate = useNavigate();
  const { subjectId } = useParams();

  const uniqueDaysCount = useMemo(() => {
    const uniqueDays = new Set();
    data.forEach(item => {
      if (item.trial_date) {
        // Extract the date portion (e.g., "2024-12-04") from "2024-12-04 15:29:28"
        const dateOnly = item.trial_date.split(' ')[0];
        uniqueDays.add(dateOnly);
      }
    });
    return uniqueDays.size;
  }, [data]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleRowClick = (trial_id) => {
    const newPath = `/dashboard/${subjectId}/trials/${trial_id}`;
    
    Global.localAppHandler.app_set_active_trial(trial_id).then(() => {
      console.log("SET ACTIVE TRIAL")
      console.log(newPath)
      navigate(newPath);
    }).catch(() => {
      console.log("this should never happen");
    });
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    Global.localAppHandler.app_get_report(Global.localAppHandler.active_subject, report.report_id);
  };

  // Define columns for react-table
  const columns = useMemo(() => [
    {
      Header: 'Trial Name',
      accessor: 'trial_name',
    },
    {
      Header: 'Trial Date',
      accessor: 'trial_date',
      sortType: (rowA, rowB) => {
        const dateA = new Date(rowA.original.trial_date);
        const dateB = new Date(rowB.original.trial_date);
        return dateA - dateB;
      },
    },
  ], []);

  // Initialize react-table instance
  const tableInstance = useTable(
    { columns, data },
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const statusText = report == null ? "Not Ready" : "Not Reviewed";
  const statusStyle = report == null ? {} : { color: 'darkgoldenrod' };

  return (
    <div className="mb-4 border rounded bg-white px-3">
      <div
        className="py-3 bg-white"
        onClick={handleToggle}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex align-items-center">
          {/* Icon on the left */}
          <div className="me-3">
            {index === 0 ? (
              <Calendar size={36} color="var(--bs-primary)" />
            ) : (
              <Clock size={36} color="var(--bs-secondary)" />
            )}
          </div>
          <div className="flex-grow-1">
            {/* First row: title and right-side actions */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="h5">
                {index === 0 ? <strong>{title}</strong> : title}
              </span>
              <div className="d-flex align-items-center">
                {report && report.is_ready ? (
                  <a href="#" className="mr-3 btn btn-link p-0" onClick={handleDownload}>
                    Download Report
                  </a>
                ) : (
                  <div className="mr-3 btn p-0">Report Not Ready</div>
                )}
                {open ? <ChevronUp /> : <ChevronDown />}
              </div>
            </div>
            {/* Second row: additional info */}
            <div className="text-muted small">
              Days Collected: {uniqueDaysCount} | <span style={statusStyle}>{statusText}</span>
            </div>
          </div>
        </div>
      </div>
      {open && (
        <table className="table table-bordered bg-white" {...getTableProps()}>
          <thead className="thead-light">
            {headerGroups.map((headerGroup, headerGroupIdx) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroupIdx}>
                {headerGroup.headers.map((column, columnIdx) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())} key={columnIdx}>
                    {column.render('Header')}
                    {!column.disableSortBy && column.isSorted ? (
                      column.isSortedDesc ? (
                        <img src={require("../../images/down_arrow.png")} alt="Down Arrow" />
                      ) : (
                        <img src={require("../../images/up_arrow.png")} alt="Up Arrow" />
                      )
                    ) : (
                      !column.disableSortBy && <img src={require("../../images/default.png")} alt="Default Arrow" />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row, rowIdx) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    key={rowIdx}
                    onMouseEnter={() => setHoveredRowIndex(rowIdx)} // Set hovered row index on mouse enter
                    onMouseLeave={() => setHoveredRowIndex(null)} // Clear hovered row index on mouse leave
                    onClick={() => handleRowClick(row.original.trial_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.cells.map((cell, cellIdx) => (
                      <td
                        {...cell.getCellProps()}
                        key={cellIdx}
                        className={hoveredRowIndex === rowIdx ? "text-primary" : ""} // Apply "text-primary" class on hover
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length}>No Trials Recorded</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DropdownTable;
